import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, Page } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { Head } from '@zg-rentals/next/app';

import { useGetPropertiesList } from '../../hooks/queries/properties/zrm';
import { usePropertiesPageUserOnboarding } from '../../hooks/usePropertiesPageUserOnboarding';
import accessUtils from '../../utils/accessUtils';
import {
  removeQueryParams,
  renderInitialDisplayModal,
  transformFiltersToApiParams,
} from '../../utils/propertiesPageUtils';

import PropertiesConstants from '../../constants/PropertiesConstants';
import QueryConstants from '../../constants/QueryConstants';
import { EXTERNAL_ROUTES } from '../../constants/urls';

import { useAuth } from '../../components/AuthProvider';
import AddFeedListingsModal from '../../components/modals/AddFeedListingsModal';
import AddPropertyModal from '../../components/modals/AddPropertyModal';
import PaymentsAggregateViewModal from '../../components/modals/PaymentsAggregateViewModal';
import UserOnboarding from '../../components/modals/UserOnboarding';
import EmailVerification from '../../components/properties/EmailVerification';
import { FlexiblePropertyEmptyState } from '../../components/properties/FlexiblePropertyEmptyState';
import NotificationBanner from '../../components/properties/NotificationBanner';
import { PropertiesPageNotifications } from '../../components/properties/PropertiesPageNotifications';

import AuthCheckWrapper from '../AuthCheckWrapper';
import { PageHeading } from '../PropertiesPage/PageHeading';
import { PropertiesPageSkeleton } from '../PropertiesPage/PropertiesPageSkeleton';

const defaultFilters = {
  searchText: PropertiesConstants.PROPERTIES_SEARCHTEXT_DEFAULT,
  subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
};

/*
 * TODO: Parity gaps vs PropertiesPage:
 *
 * 1. Analytics - emitViewPropertiesPageEvent on mount, data-eventid on Page.Root,
 *    and add-property/add-feed clickstream events.
 *
 * 2. Properties list / filters - Filters and PropertyTable.
 */
const FlexiblePropertiesPage = () => {
  const router = useRouter();
  const { user } = useAuth();

  const isCheckInitialDisplayModal = useRef(true);

  const [showAddFeedListingsModal, setShowAddFeedListingsModal] = useState(false);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showPaymentsAggregateViewModal, setShowPaymentsAggregateViewModal] = useState(false);

  const skeleton = <PropertiesPageSkeleton />;

  const {
    data: propertiesData,
    error: propertiesError,
    isError,
    isPending,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetPropertiesList(transformFiltersToApiParams(defaultFilters), {
    enabled: Boolean(user?.loggedIn && user?.restrictions?.isAllowed),
  });

  const { listingGroups = [], listingCount } = propertiesData ?? { listingCount: undefined, pager: undefined };

  const { total: numTotalListings = 0, archived: numArchivedListings = 0 } = listingCount ?? {};

  const {
    canAccessUserOnboardingNewUser,
    setShowingUserOnboardingExisitngUser,
    canAccessUserOnboardingExistingUser,
    onboardingConfig,
  } = usePropertiesPageUserOnboarding({ numTotalListings });

  const isContentLoading = isPending || isFetching || isFetchingNextPage;
  const hasAnyListings = numTotalListings > 0 || numArchivedListings > 0;

  useEffect(() => {
    if (
      user?.features?.canAccessMainPayments &&
      !user?.hasSeenPaymentsAggregatedViewModal &&
      (user?.paymentsAcceptedCount ?? 0) >= 2
    ) {
      setShowPaymentsAggregateViewModal(true);
    }
  }, [user?.features?.canAccessMainPayments]);

  useEffect(() => {
    if (isCheckInitialDisplayModal.current && !isPending) {
      isCheckInitialDisplayModal.current = false;
      checkInitialDisplayModal();
    }
  }, [isPending, numTotalListings]);

  useEffect(() => {
    if (isError && propertiesError) {
      logError({
        error: propertiesError,
        context: 'fetchGroupableProperties',
      });
    }
  }, [isError, propertiesError]);

  useEffect(() => {
    const postingPath = router.query[QueryConstants.POSTING_PATH];
    const addAFeed = router.query[QueryConstants.ADD_A_FEED];

    if (postingPath || addAFeed) {
      checkInitialDisplayModal();
    }
  }, [router.query[QueryConstants.POSTING_PATH], router.query[QueryConstants.ADD_A_FEED]]);

  const checkInitialDisplayModal = () => {
    const propertiesForModal = {
      listings: listingGroups.map((group) => ({ listingAlias: group.listingAlias })),
      listingCount: listingCount ?? {},
    };

    renderInitialDisplayModal({
      router,
      totalListingsWithoutFilters: numTotalListings,
      showAddPropertyModal,
      setShowAddPropertyModal: handleAddPropertyClick,
      canAccessUserOnboardingNewUser,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      properties: propertiesForModal as any,
      setShowAddFeedListingsModal: () => {
        if (user?.restrictions?.isAllowed && accessUtils.canAccessAddFeedListingsModal({ user })) {
          setShowAddFeedListingsModal(true);
        }
      },
      setShowingUserOnboardingExisitngUser,
      canAccessUserOnboardingExistingUser,
    });
  };

  const handleAddPropertyClick = ({ shouldSendAnalytics = false }: { shouldSendAnalytics?: boolean } = {}) => {
    if (user?.features?.canAddListing && user?.restrictions?.isAllowed) {
      setShowAddPropertyModal(true);
    }

    if (shouldSendAnalytics) {
      // TODO: add flexible properties analytics
      // emitOpenEvent({ user: user as ZrmClickstreamUser });
      // emitAddPropertyClickEvent({ user: user as ZrmClickstreamUser });
    }
  };

  const handleAddFeedListingsClick = () => {
    setShowAddFeedListingsModal(true);
    // TODO: add flexible properties analytics
    // emitAddFeedClickEvent({ user: user as ZrmClickstreamUser });
  };

  const canAccessFeatureNotifications = user ? accessUtils.canAccessFeatureNotifications({ user }) : false;

  return (
    <AuthCheckWrapper loading={skeleton} redirecting={skeleton}>
      <Page.Root data-testid="FlexiblePropertiesPage" css={{ backgroundColor: 'bg.neutral' }}>
        <Head applicationName="zillow-rental-manager-web" buildNumber={process.env.NEXT_PUBLIC_BUILD_NUMBER}>
          <title>Properties | Zillow Rental Manager</title>
        </Head>

        {canAccessFeatureNotifications && <NotificationBanner />}

        {/* Page Heading */}
        <PageHeading
          buttonEmphasis="filled"
          buttonIconPosition="start"
          buttonIcon={<IconPlusFilled />}
          buttonSize="sm"
          headingTextStyle="heading-xs"
          handleAddFeedListingsClick={handleAddFeedListingsClick}
          handleAddPropertyClick={handleAddPropertyClick}
        />

        <EmailVerification />
        <PropertiesPageNotifications />

        {/* Content / Tables */}
        <Page.Content css={{ padding: { base: '0 {spacing.600}', xl: '0 {spacing.1200}' } }}>
          {!hasAnyListings && !isContentLoading && (
            <FlexiblePropertyEmptyState onAddPropertyClick={handleAddPropertyClick} />
          )}

          {hasNextPage && (
            <Box css={{ display: 'flex', justifyContent: 'center', marginTop: '600' }}>
              <Button tone="brand" emphasis="filled" onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
                Load more properties
              </Button>
            </Box>
          )}
        </Page.Content>

        {/* Modals */}
        {showAddFeedListingsModal && (
          <AddFeedListingsModal
            onClose={() => {
              setShowAddFeedListingsModal(false);
              removeQueryParams(router, [QueryConstants.ADD_A_FEED]);
            }}
          />
        )}
        {onboardingConfig && <UserOnboarding {...onboardingConfig} />}

        {showAddPropertyModal && (
          <AddPropertyModal
            isOpen={showAddPropertyModal}
            onClose={() => {
              setShowAddPropertyModal(false);
              removeQueryParams(router, [QueryConstants.POSTING_PATH, QueryConstants.REDIRECT_TO]);
            }}
          />
        )}
        {showPaymentsAggregateViewModal && (
          <PaymentsAggregateViewModal
            onClose={() => setShowPaymentsAggregateViewModal(false)}
            paymentsAggregatePageUrl={EXTERNAL_ROUTES.PAYMENTS_DASHBOARD}
          />
        )}
      </Page.Root>
    </AuthCheckWrapper>
  );
};

export default FlexiblePropertiesPage;
